import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import flags from "../features/featureFlags/flags";

import {
  selectMPSTopicFile,
  selectDitaPath,
  selectHeadTitle,
  selectTocId,
  selectActiveTopicHRefSearchPath,
} from "../features/counter/counterSlice";
import { removeSpecialCharacters } from "../utils";
import useAppModernisation from "../hooks/useAppModernisation";
import AppTopicBanner from "./AppTopicBanner/AppTopicBanner";

const Breadcrumb = React.lazy(() => import("./breadcrumb"));
const TopicTools = React.lazy(() => import("./topicTools"));

const TopicBanner = ({
  push,
  expandExpertGroupsSideBar,
  expandUpdatesSideBar,
  setShowTopicActionBar,
}) => {
  const { pathname, search, hash } = useLocation();
  const isEnhancedBrowserHistoryButton = useBooleanFlagValue(
    flags.ENHANCED_BROWSER_HISTORY_BUTTON,
    false,
  );
  const [headerClass, setHeaderClass] = useState("");
  const [isTopicBanner, setIsTopicBanner] = useState(false);
  const mpstopicfile = useSelector(selectMPSTopicFile);
  const ditapath = useSelector(selectDitaPath);
  const headTitle = useSelector(selectHeadTitle);
  const tocId = useSelector(selectTocId);
  const activeTopicHRefSearchPath = useSelector(selectActiveTopicHRefSearchPath);
  const { isAppMode } = useAppModernisation();

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    setIsTopicBanner(false);
    if (pathname.includes("searchAction") || pathname.includes("patientInfo")) {
      setHeaderClass("search-page");
    } else if (pathname.includes("/calculator/")) {
      setHeaderClass("calculator-page");
    } else if (pathname.includes("/userConfirmation")) {
      setHeaderClass("userConfirmation-page");
    } else if (pathname.includes("/myCart")) {
      setHeaderClass("myCart-page");
    } else if (pathname.includes("/checkout")) {
      setHeaderClass("checkout-page");
    } else if (pathname.includes("/showMyProfile")) {
      setHeaderClass("showMyProfile-page");
    } else if (pathname.includes("viewTopic") || pathname.includes("topicTeaser")) {
      const guidelinePage = searchParams.get("guidelinePage");
      const cleanDitapath = removeSpecialCharacters(ditapath);
      setIsTopicBanner(true);

      if (cleanDitapath === "") {
        return;
      }

      let cleanGuidelinePage = removeSpecialCharacters(guidelinePage);
      cleanGuidelinePage = cleanGuidelinePage ? cleanGuidelinePage.replace(/ /g, "_") : "";
      if (cleanGuidelinePage === "" || cleanDitapath === cleanGuidelinePage) {
        if (mpstopicfile[cleanDitapath]) {
          const className = mpstopicfile[cleanDitapath]?.bannerclass;
          setHeaderClass(className);
        } else {
          console.error(`No object found in mpstopicfile for ditapath: ${ditapath}`);
        }
      } else if (cleanDitapath !== cleanGuidelinePage) {
        const className = mpstopicfile[cleanGuidelinePage.replace("/", "")]?.bannerclass;
        setHeaderClass(className);
      } else {
        setHeaderClass("book-other");
      }
    }
  }, [ditapath, pathname, search]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const bannerText = useMemo(() => {
    const bannerMapping = {
      "/viewTopic/": headTitle || tocId,
      "/viewTopic": headTitle || tocId,
      "/searchAction/": "Search Results",
      "/calculator/": "Calculators",
      "/": "Therapeutic Guidelines",
      "/patientInfo/": "Patient information and useful PDFs",
      "/import-classic/": "Import",
      "/myCart": "My Cart",
      "/userConfirmation": "User Confirmation",
      "/checkout": "Checkout",
      "/showMyProfile": "My account",
    };
    // activeTopicHRefSearchPath is having data when user selects any topic
    if (
      isEnhancedBrowserHistoryButton &&
      pathname === "/viewTopic" &&
      activeTopicHRefSearchPath?.length > 0
    ) {
      const currentUrl = `${pathname}${search}${hash}`;
      const match = activeTopicHRefSearchPath
        .slice()
        .reverse()
        .find((entry) => entry?.mpsPath === currentUrl);
      if (match?.header) {
        console.log("match>>>>>", match.header);
        return match.header;
      }
    }
    for (const [key, value] of Object.entries(bannerMapping)) {
      if (pathname === key || pathname.includes(key)) {
        return value;
      }
    }
  }, [pathname, search, hash, headTitle, tocId]);

  return isAppMode && isTopicBanner ? (
    <AppTopicBanner
      pathname={pathname}
      search={search}
      hash={hash}
      expandExpertGroupsSideBar={expandExpertGroupsSideBar}
      expandUpdatesSideBar={expandUpdatesSideBar}
      setShowTopicActionBar={setShowTopicActionBar}
    />
  ) : (
    <div id="root">
      <div className="page-heading">
        <div className="container">
          <Breadcrumb
            guideline={ditapath.replace(/_/g, " ").replace(/\//g, "")}
            push={push}
            topic={headTitle ?? ""}
            pathname={pathname}
            mpstopicfile={mpstopicfile}
          />
        </div>
        <div className="viewTopic-banner">
          <input type="hidden" id="topicHighlight" value="" />
          <div className={`page-header2 ${headerClass}`}>
            <div className="container">
              <h1 id="topic-banner">{bannerText}</h1>
            </div>
          </div>
          {pathname.includes("viewTopic") && <TopicTools locationSearch={search} />}
        </div>
      </div>
    </div>
  );
};

export default TopicBanner;
